import { useLazyQuery, useQuery } from '@apollo/client/react';
import { STAFF_ACCOUNT_AUDIT_LOGS, STAFF_ACCOUNT_AUDIT_RESOLVE } from '@staff-app/graphql-client';

/** The customer a lookup resolved to. */
export interface AuditAccount {
  accountId: string;
  companyName?: string | null;
  ownerEmail?: string | null;
  stripeId?: string | null;
  /** Set when the searched `cus_` was a team's; the log shown is its parent account's. */
  matchedTeam?: string | null;
}

/** One audit-log row (display-mapped server-side; `time` is CST). Customer, or Super User. */
export interface AccountAuditEntry {
  id: string;
  createdAt: string;
  time: string;
  action?: number | null;
  type?: number | null;
  /** HTML as open-api wrote it — rendered as plain text, never as markup. */
  text?: string | null;
  teamId?: string | null;
  teamName?: string | null;
  userId?: string | null;
  /** Who did it, stored with the row (from 2026-10); absent on older rows. */
  userName?: string | null;
  /** For a Super User row this IS the staff email (`staffEmail`). */
  userEmail?: string | null;
  /** `text` without its actor (from 2026-10); absent on older rows → show `text`. */
  description?: string | null;
  ipAddress?: string | null;
  /** Staff acting through "Login as Customer" — the customer sees these as "OptiSigns Support". */
  isSuperUser?: boolean | null;
  staffEmail?: string | null;
}

export interface AccountAuditPage {
  items: AccountAuditEntry[];
  /** Counted up to 10,000. */
  totalCount: number;
  /** The count stopped at the cap; the real total is at least `totalCount`. */
  totalCapped: boolean;
  /** The CST day range the server actually applied (its 30-day default when none was sent). */
  dateFrom: string;
  dateTo: string;
}

/** Whose rows to show: both (default), the customer's users only, or Super Users only. */
export type AccountAuditSource = 'ALL' | 'CUSTOMER' | 'SUPER_USER';

export interface AccountAuditFilter {
  source?: AccountAuditSource;
  actions?: number[];
  description?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AccountAuditVars {
  accountId: string;
  filter?: AccountAuditFilter;
  pagination?: { offset: number; limit: number };
}

export function useAccountAuditResolve() {
  return useLazyQuery<{ staffAccountAuditResolve: AuditAccount }, { query: string }>(
    STAFF_ACCOUNT_AUDIT_RESOLVE,
    { fetchPolicy: 'network-only' },
  );
}

/** Filters and paging run SERVER-SIDE across the account's whole retained history. */
export function useAccountAuditLogs(vars: AccountAuditVars | null) {
  return useQuery<{ staffAccountAuditLogs: AccountAuditPage }, AccountAuditVars>(
    STAFF_ACCOUNT_AUDIT_LOGS,
    {
      variables: vars ?? { accountId: '' },
      skip: !vars,
      fetchPolicy: 'cache-and-network',
    },
  );
}
